import Button from '@/features/openTabs/components/AccountActionButton'
import { OPEN_ACCOUNT_NAME_MAX_LENGTH } from '@/features/openTabs/constants'
import { useOpenTabModalBack } from '@/features/openTabs/hooks/useOpenTabModalBack'
import Modal from '@/features/ui/components/Modal'
import InputField from '@/features/ui/inputs/components/InputField'
import Typography from '@/features/ui/typography'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { View } from 'react-native'
import { createStyleSheet } from 'react-native-unistyles'

interface CreateAccountModalProps {
    name: string
    title?: string
    confirmLabel?: string
    loading?: boolean
    onChange: (name: string) => void
    onConfirm: () => void | Promise<void>
    onCancel: () => void
}

const CreateAccountModal: FC<CreateAccountModalProps> = ({
    name,
    title,
    confirmLabel,
    loading = false,
    onChange,
    onConfirm,
    onCancel,
}) => {
    const { t } = useTranslation('app')
    useOpenTabModalBack(onCancel, { disabled: loading })

    return (
        <Modal avoidKeyboard>
            <View style={styles.container}>
                <Typography.Heading variant="h3" style={styles.title}>
                    {title ?? t('openTabs.createAccount.title')}
                </Typography.Heading>
                <View style={styles.fieldContainer}>
                    <Typography.Description font="medium">
                        {t('openTabs.createAccount.nameLabel')}
                    </Typography.Description>
                    <InputField
                        autoFocus
                        disabled={loading}
                        maxLength={OPEN_ACCOUNT_NAME_MAX_LENGTH}
                        onChange={onChange}
                        onSubmitEditing={onConfirm}
                        placeholder={t('openTabs.createAccount.namePlaceholder')}
                        returnKeyType="done"
                        style={styles.input}
                        value={name}
                    />
                </View>
                <View style={styles.actions}>
                    <Button
                        disabled={name.trim().length > OPEN_ACCOUNT_NAME_MAX_LENGTH || loading}
                        onPress={onConfirm}
                        style={styles.button}
                        color="secondary"
                    >
                        {confirmLabel ?? t('openTabs.createAccount.confirm')}
                    </Button>
                    <Button
                        disabled={loading}
                        onPress={onCancel}
                        style={styles.button}
                        color="secondary"
                        type="outline"
                    >
                        {t('openTabs.actions.cancel')}
                    </Button>
                </View>
            </View>
        </Modal>
    )
}

const styles = createStyleSheet({
    title: { textAlign: 'center' },
    container: {
        paddingHorizontal: 16,
        paddingVertical: 32,
        gap: 32,
    },
    fieldContainer: {
        gap: 8,
    },
    input: {
        width: '100%',
    },
    actions: {
        gap: 16,
    },
    button: {
        width: '100%',
        borderRadius: 8,
        justifyContent: 'center',
    },
})

export default CreateAccountModal
